import { ErrorBoundary, For, Show, createEffect, createSignal, onMount } from "solid-js";
import type { User } from "system";
import { GlowTuner } from "~/components/glow-tuner";
import { Navbar } from "~/components/layout/navbar";
import { OfflineIndicator } from "~/components/offline-indicator";
import { Button } from "~/components/ui/button";
import { ErrorFallback } from "~/components/ui/error-fallback";
import { useAuth } from "~/lib/auth";
import { gcCardBgCache, prewarmCard, prewarmPageBackdrop } from "~/lib/card-backgrounds";
import { RouterContext } from "~/router";

import { Outlet, createRootRouteWithContext, useNavigate, useRouter, useRouterState } from "@tanstack/solid-router";

export const Route = createRootRouteWithContext<RouterContext>()({
  errorComponent: ({ error, reset }) => <ErrorFallback error={error} reset={reset} />,
  component: () => {
    const isLanding = useRouterState({ select: (s) => s.location.pathname === "/" });
    const router = useRouter();
    const { state } = useAuth();

    // When auth finishes loading, re-run all beforeLoad guards so routes that
    // passed during loading now redirect correctly with the resolved auth state.
    let invalidated = false;
    createEffect(() => {
      if (!state.loading && !invalidated) {
        invalidated = true;
        router.invalidate();
      }
    });

    // Pre-warm the current user's card background bitmap and page backdrop
    // as soon as auth resolves,
    // so /dashboard and /profile/me render instantly on first nav.
    // Also GC orphan IDB entries from prior deploys whose URLs no longer
    // exist in the catalog (handles re-triage / bucket reshuffles).
    let prewarmed = false;
    createEffect(() => {
      const u = state.user;
      if (u && !prewarmed) {
        prewarmed = true;
        prewarmCard(u.driverNumber);
        prewarmPageBackdrop();
        gcCardBgCache();
      }
    });

    return (
      <div class="bg-surface-1 overflow-x-clip min-h-dvh flex flex-col" classList={{ "pb-[calc(4rem_+_env(safe-area-inset-bottom,0px))] md:pb-0": !!state.user && !isLanding() }}>
        <Show when={!isLanding()}>
          <Navbar />
        </Show>
        <ErrorBoundary fallback={(err, reset) => <ErrorFallback error={err} reset={reset} />}>
          <Outlet />
        </ErrorBoundary>
        <Show when={import.meta.env.DEV && !state.user && !state.loading}>
          <DevLoginPanel />
        </Show>
        <GlowTuner />
        <OfflineIndicator />
      </div>
    );
  },
});

function DevLoginPanel() {
  const { devLogin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = createSignal<User[]>([]);
  const [selected, setSelected] = createSignal("");
  const [pending, setPending] = createSignal(false);

  onMount(async () => {
    const res = await fetch("/api/auth/dev/users");
    if (res.ok) setUsers(await res.json());
  });

  async function handleLogin() {
    const id = selected();
    if (!id) return;
    setPending(true);
    try {
      await devLogin(id);
      navigate({ to: "/dashboard" });
    } finally { setPending(false); }
  }

  return (
    <div class="fixed bottom-4 right-4 z-50 flex flex-col gap-2 bg-surface-2 border border-border-subtle rounded-lg p-3 shadow-lg w-60">
      <p class="text-[10px] text-text-faint uppercase tracking-widest font-semibold">Dev Login</p>
      <select
        class="text-sm bg-surface-3 border border-border-subtle rounded px-2 py-1.5 text-text-base w-full"
        value={selected()}
        onChange={(e) => setSelected(e.currentTarget.value)}
      >
        <option value="">Select user…</option>
        <For each={users()}>
          {(u) => <option value={u.id}>{u.displayName} [{u.role}]</option>}
        </For>
      </select>
      <Button size="sm" disabled={!selected() || pending()} onClick={handleLogin}>
        {pending() ? "…" : "Login as selected"}
      </Button>
    </div>
  );
}

